import { ExpressionNode, IdentifierNode, ProgramNode } from "./ast";
import { Selection } from "./selection";
import { Suggestions } from "./suggestions";

/**
 * Recurses until it finds the end of the expression/property chain and then attaches the identifier.
 */
const walk = (node: ExpressionNode, identifier: IdentifierNode, suggestions: Suggestions): void => {
  /**
   * Expression is `${}`, value is `bar`, outcome is `${bar}`
   */
  if (!node.identifier) {
    node.identifier = identifier;
    node.property = undefined;
    return;
  }

  /**
   * Drill into the next level of suggestions. If the value is true, then we've found the end of the chain.
   * If it's undefined, then assume the last expression is a search term, so we should _replace_ the identifier.
   */
  const next = suggestions[node.identifier.value];

  if (next === undefined || next === true) {
    node.identifier = identifier;
    node.property = undefined;

    return;
  }

  /**
   * Expression is `${foo}`, value is `bar`, outcome is `${foo.bar}`
   */
  if (!node.property) {
    node.property = {
      type: "Expression",
      identifier,
      property: undefined,
      start: -1,
      end: -1,
    };

    return;
  }

  walk(node.property, identifier, next);
};

export const append = (program: ProgramNode, selection: Selection, suggestions: Suggestions, value: string): ProgramNode => {
  const node = program.body.find((node) => node.start <= selection.start && node.end >= selection.end);

  if (node?.type === "Expression") {
    const identifier: IdentifierNode = {
      type: "Identifier",
      value,
      start: -1,
      end: -1,
    };

    walk(node, identifier, suggestions);
  }

  return program;
};
